/**
 * What each plan allows — the single definition, used by both sides.
 *
 * This lived in `worker/storage.ts`, and the client kept its own copy of the
 * project counts in `src/auth/session.ts`. Two copies of a number the user is
 * shown and the server enforces is a drift waiting to happen: the day the free
 * quota moves, one of them says 3 and the other says 5, and whichever the UI
 * uses is now lying about what will actually be refused.
 *
 * Kept free of Workers types on purpose so the browser can import it.
 */

const MB = 1024 * 1024;
const GB = 1024 * MB;

export type Tier = "free" | "creator" | "studio";
export const TIERS: Tier[] = ["free", "creator", "studio"];

export interface TierLimits {
  cloudSave: boolean;
  versionRetention: number;
  maxProjects: number;
  /**
   * Total stored bytes across every version the user keeps.
   *
   * The binding constraint on cost, and the only one that is. Project and
   * version counts bound the *number* of objects; this bounds their size, which
   * is what R2 actually bills. Set so the storage cost of a full account is a
   * rounding error against its subscription: 2 GB is about $0.03/month.
   */
  maxStorageBytes: number;
}

export const TIER_LIMITS: Record<Tier, TierLimits> = {
  /**
   * Free accounts get a real, small cloud allowance rather than nothing.
   *
   * A free tier that grants only "identity and the ability to pay" gives nobody
   * a reason to sign up, and leaves the anonymous-to-account migration with
   * nowhere to put anything. Three projects costs roughly 1.5 MB — about
   * $0.00002/user/month of R2 — and turns the paywall into an earned moment
   * ("that's your third saved design") instead of a wall.
   *
   * See docs/decisions/sign-in.md.
   */
  free: {
    cloudSave: true,
    versionRetention: 3,
    maxProjects: 3,
    maxStorageBytes: 100 * MB,
  },
  creator: {
    cloudSave: true,
    versionRetention: 25,
    maxProjects: 500,
    maxStorageBytes: 2 * GB,
  },
  studio: {
    cloudSave: true,
    versionRetention: 100,
    maxProjects: 5000,
    maxStorageBytes: 20 * GB,
  },
};

/** Display name. Used in the account menu, billing panel and upgrade prompts. */
export const TIER_NAMES: Record<Tier, string> = {
  free: "Free",
  creator: "Creator",
  studio: "Studio",
};

/**
 * The glyph that stands for each plan.
 *
 * Sprite ids from the editor's own sheet, so a plan is marked by a tool the
 * reader will actually meet rather than by an invented badge — and they
 * escalate the way the plans do: one brush, a palette, a set of colourways.
 *
 * The matching colours are design tokens, not strings, because a hex here
 * would be a fourth place the brand lives. See --tier-*-tint / -wash / -edge.
 */
export const TIER_ICONS: Record<Tier, string> = {
  free: "i-brush",
  creator: "i-palette",
  studio: "i-colourways",
};

/**
 * The one-line promise of each plan.
 *
 * "Yourself / seriously / for other people" is the whole decision spine — a
 * visitor self-selects in about four seconds, which thirteen rows of checkmarks
 * never let them do. See docs/decisions/pricing-and-checkout.md.
 */
export const TIER_TAGLINES: Record<Tier, string> = {
  free: "Make one shirt, correctly.",
  creator: "Make a lot of shirts, well.",
  studio: "Make shirts for other people.",
};

export function limitsFor(tier: Tier): TierLimits {
  return TIER_LIMITS[tier] ?? TIER_LIMITS.free;
}

export function remainingProjectQuota(tier: Tier, used: number): number {
  return Math.max(0, limitsFor(tier).maxProjects - used);
}

/**
 * Is this project past the quota, and therefore read-only?
 *
 * `rank` is 1-based by recency. Downgrading never deletes anything — it takes
 * away writes to the overflow, so the archive freezes and the projects someone
 * is actually working on keep working. The server decides; this exists so the
 * UI can show the same answer without asking.
 */
export function isOverQuota(rank: number, tier: Tier): boolean {
  return rank > limitsFor(tier).maxProjects;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < MB) return `${Math.round(bytes / 1024)} KB`;
  if (bytes < GB) return `${(bytes / MB).toFixed(bytes < 10 * MB ? 1 : 0)} MB`;
  return `${(bytes / GB).toFixed(1)} GB`;
}
